import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Actions,
  Async,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { useDebounce } from '@client/hooks/use-debounce';
import { useInfluencerListModel } from '../model/InfluencerList.model';
import { Influencer } from '../model/InfluencerList.types';
import {
  InfluencerSortField,
  InfluencerSorting,
  sortInfluencers,
  toAvatarUrl,
  toBusinessModelLabel,
  toDateLabel,
  toFullName,
  toggleSort,
} from './InfluencerList.viewModel.mappers';
import {
  FilterRule,
  FilterFieldConfig,
  FILTER_FIELD_CONFIGS,
} from './InfluencerList.filterTypes';
import { applyAllFilterRules } from './InfluencerList.filterEngine';

const STORAGE_KEY = 'influencerListFilters';

type StoredFilters = {
  search: string;
  filterRules: FilterRule[];
  sorting: InfluencerSorting;
};

const loadFilters = (): Partial<StoredFilters> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};

    return JSON.parse(raw) as Partial<StoredFilters>;
  } catch {
    return {};
  }
};

const saveFilters = (filters: StoredFilters): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
};

export type InfluencerRowVM = {
  id: number;
  fullName: string;
  truncatedName: string;
  customURL: string;
  avatarUrl: string;
  businessModelLabel: string;
  tier: number | null;
  createdAtLabel: string;
  publishedAtLabel: string;
  instagramFollowersCount: number | null;
  profileInstagram: string | null;
  profileTiktok: string | null;
  profileYoutubeChannel: string | null;
  isTierUpdating: boolean;
  isDeleting: boolean;
};

type InfluencerListActionMap = {
  SetSearch: string;
  AddFilterRule: FilterRule;
  RemoveFilterRule: string;
  UpdateFilterRule: FilterRule;
  ClearAllFilters: null;
  ToggleSort: InfluencerSortField;
  DeleteInfluencer: number;
};

export type InfluencerListTableVM = {
  filteredSorted: Influencer[];
  totalCount: number;
  visibleCount: number;
};

export type InfluencerListVM = {
  tableState: Async<InfluencerListTableVM>;
  toRowVM: (influencer: Influencer) => InfluencerRowVM;
  search: string;
  filterRules: FilterRule[];
  filterFieldConfigs: FilterFieldConfig[];
  sorting: InfluencerSorting;
  updateTier: (p: { influencerId: number; tier: number }) => Promise<void>;
} & Dispatchers<InfluencerListActionMap>;

const filterBySearch = (
  influencers: Influencer[],
  search: string,
): Influencer[] => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return influencers;

  return influencers.filter((influencer) =>
    [
      toFullName(influencer),
      influencer.customURL,
      influencer.id.toString(),
      influencer.firstName,
      influencer.lastName,
    ]
      .join(' ')
      .toLowerCase()
      .includes(normalizedSearch),
  );
};

const MAX_NAME_LENGTH = 26;

const toTruncatedName = (fullName: string) =>
  fullName.length > MAX_NAME_LENGTH
    ? `${fullName.substring(0, MAX_NAME_LENGTH)}...`
    : fullName;

export const useInfluencerListViewModel = () => {
  const stored = useMemo(loadFilters, []);

  const [search, setSearch] = useState(stored.search ?? '');
  const [filterRules, setFilterRules] = useState<FilterRule[]>(
    stored.filterRules ?? [],
  );
  const [sorting, setSorting] = useState<InfluencerSorting>(
    stored.sorting ?? { field: 'name', direction: 'asc' },
  );
  const [updatingTierIds, setUpdatingTierIds] = useState<number[]>([]);
  const [deletingIds, setDeletingIds] = useState<number[]>([]);

  const debouncedSearch = useDebounce(search, 200);

  const asyncModel = useInfluencerListModel();
  const asyncModelRef = useRef(asyncModel);
  asyncModelRef.current = asyncModel;

  useEffect(() => {
    saveFilters({ search, filterRules, sorting });
  }, [search, filterRules, sorting]);

  const updateTier = useCallback(
    async (p: { influencerId: number; tier: number }): Promise<void> => {
      if (asyncModelRef.current.type !== 'fetched') return;

      setUpdatingTierIds((prev) => [...prev, p.influencerId]);

      try {
        await asyncModelRef.current.value.updateTier(p);
        toast.success('Tier updated successfully');
      } catch {
        toast.error('Something went wrong while updating tier');
      } finally {
        setUpdatingTierIds((prev) =>
          prev.filter((currentId) => currentId !== p.influencerId),
        );
      }
    },
    [],
  );

  const deleteInfluencer = useCallback(
    async (influencerId: number): Promise<void> => {
      if (asyncModelRef.current.type !== 'fetched') return;

      setDeletingIds((prev) => [...prev, influencerId]);

      try {
        await asyncModelRef.current.value.deleteInfluencer(influencerId);
        toast.success('Creator removed');
      } catch {
        toast.error('Could not delete creator');
      } finally {
        setDeletingIds((prev) =>
          prev.filter((currentId) => currentId !== influencerId),
        );
      }
    },
    [],
  );

  const _dispatch = (action: Actions<InfluencerListActionMap>) => {
    switch (action.type) {
      case 'SetSearch':
        setSearch(action.payload);
        return;

      case 'AddFilterRule':
        setFilterRules((prev) => [...prev, action.payload]);
        return;

      case 'RemoveFilterRule':
        setFilterRules((prev) =>
          prev.filter((rule) => rule.id !== action.payload),
        );
        return;

      case 'UpdateFilterRule':
        setFilterRules((prev) =>
          prev.map((rule) =>
            rule.id === action.payload.id ? action.payload : rule,
          ),
        );
        return;

      case 'ClearAllFilters':
        setFilterRules([]);
        return;

      case 'ToggleSort':
        setSorting((prev) => toggleSort(prev, action.payload));
        return;

      case 'DeleteInfluencer':
        void deleteInfluencer(action.payload);
        return;
    }
  };

  const dispatchers = getDispatchers<InfluencerListActionMap>(_dispatch);

  const tableState = useMemo((): Async<InfluencerListTableVM> => {
    if (asyncModel.type !== 'fetched') return asyncModel;

    const searchFiltered = filterBySearch(
      asyncModel.value.influencers,
      debouncedSearch,
    );
    const filteredSorted = sortInfluencers(
      applyAllFilterRules(searchFiltered, filterRules),
      sorting,
    );

    return toAsyncFetched({
      filteredSorted,
      totalCount: asyncModel.value.influencers.length,
      visibleCount: filteredSorted.length,
    });
  }, [asyncModel, debouncedSearch, filterRules, sorting]);

  const toRowVM = useCallback(
    (influencer: Influencer): InfluencerRowVM => {
      const fullName = toFullName(influencer);
      const safeFullName = fullName || 'Unnamed creator';

      return {
        id: influencer.id,
        fullName: safeFullName,
        truncatedName: toTruncatedName(safeFullName),
        customURL: influencer.customURL,
        avatarUrl: toAvatarUrl(influencer.mediaCoverPath),
        businessModelLabel: toBusinessModelLabel(influencer.businessModel),
        tier: influencer.tier,
        createdAtLabel: toDateLabel(influencer.createdAt),
        publishedAtLabel: toDateLabel(influencer.publishedAt),
        instagramFollowersCount: influencer.instagramFollowersCount,
        profileInstagram: influencer.profileInstagram,
        profileTiktok: influencer.profileTiktok,
        profileYoutubeChannel: influencer.profileYoutubeChannel,
        isTierUpdating: updatingTierIds.includes(influencer.id),
        isDeleting: deletingIds.includes(influencer.id),
      };
    },
    [updatingTierIds, deletingIds],
  );

  return toAsyncFetched<InfluencerListVM>({
    tableState,
    toRowVM,
    search,
    filterRules,
    filterFieldConfigs: FILTER_FIELD_CONFIGS,
    sorting,
    updateTier,
    ...dispatchers,
  });
};
